import { useState } from 'react';
import Modal from '../../components/common/Modal';
import SearchableSelect from '../../components/common/SearchableSelect';
import { PERFIL_OPCOES } from '../../utils/acreditacaoOpcoes';
import { useAuth } from '../../contexts/AuthContext';
import * as usuariosApi from '../../api/usuarios';

export default function UsuarioForm({ usuario, hospitais, onClose, onSalvar }) {
  const { user: usuarioLogado } = useAuth();
  const [nome, setNome] = useState(usuario?.nome || '');
  const [email, setEmail] = useState(usuario?.email || '');
  const [senha, setSenha] = useState('');
  const [perfil, setPerfil] = useState(usuario?.perfil || 'operador');
  const [idHospital, setIdHospital] = useState(usuario?.id_hospital || '');
  const [ativo, setAtivo] = useState(usuario?.ativo ?? true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const [mostrarResetSenha, setMostrarResetSenha] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
  const [resetando, setResetando] = useState(false);
  const [resetSenhaSalva, setResetSenhaSalva] = useState(false);
  const [erroResetSenha, setErroResetSenha] = useState('');

  const perfilOpcoes = PERFIL_OPCOES.filter((p) => p.value !== 'admin' || usuarioLogado?.perfil === 'admin');
  const hospitalOpcoes = hospitais.map((h) => ({ value: h.id_hospital, label: h.nome }));
  const podeResetarSenha = usuario && (usuario.perfil !== 'admin' || usuarioLogado?.perfil === 'admin');
  const exigeHospital = (p) => p === 'cliente' || p === 'auditor_hospital';

  const handleResetarSenha = async () => {
    setErroResetSenha('');
    setResetSenhaSalva(false);

    if (novaSenha.length < 8) {
      setErroResetSenha('A nova senha deve ter ao menos 8 caracteres.');
      return;
    }
    if (novaSenha !== confirmarNovaSenha) {
      setErroResetSenha('A confirmação não bate com a nova senha.');
      return;
    }

    setResetando(true);
    try {
      await usuariosApi.resetarSenha(usuario.id_usuario_portal, novaSenha);
      setResetSenhaSalva(true);
      setNovaSenha('');
      setConfirmarNovaSenha('');
      setMostrarResetSenha(false);
    } catch (err) {
      setErroResetSenha(err?.response?.data?.detail || 'Não foi possível redefinir a senha.');
    } finally {
      setResetando(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    if (exigeHospital(perfil) && !idHospital) {
      setErro('Selecione o hospital deste usuário.');
      return;
    }

    if (usuario && usuario.ativo && !ativo) {
      const confirmado = window.confirm(
        `Desativar ${usuario.nome}? A pessoa perde o acesso ao sistema imediatamente.`
      );
      if (!confirmado) return;
    }

    setEnviando(true);
    try {
      const dados = {
        nome,
        email,
        perfil,
        id_hospital: exigeHospital(perfil) ? idHospital : null,
      };
      if (!usuario) dados.senha = senha;
      if (usuario) dados.ativo = ativo;
      await onSalvar(dados);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível salvar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal titulo={usuario ? 'Editar usuário' : 'Novo usuário'} onClose={onClose}>
      <form className="am-form" onSubmit={handleSubmit}>
        <label className="am-field">
          <div className="am-field__label-row">
            <span className="am-field__label">Nome</span>
          </div>
          <input value={nome} onChange={(e) => setNome(e.target.value)} required />
        </label>

        <label className="am-field">
          <div className="am-field__label-row">
            <span className="am-field__label">E-mail</span>
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!!usuario}
            required
          />
        </label>

        {!usuario && (
          <label className="am-field">
            <div className="am-field__label-row">
              <span className="am-field__label">Senha provisória</span>
            </div>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              minLength={8}
              required
            />
          </label>
        )}

        <div>
          <div className="am-field__label mb-1">Permissão</div>
          <SearchableSelect
            options={perfilOpcoes}
            value={perfil}
            onChange={(v) => setPerfil(v)}
            placeholder="Selecione a permissão"
            required
          />
          <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', marginTop: 4 }}>
            {perfil === 'cliente' &&
              'Usuário do hospital-cliente: acesso somente leitura ao próprio hospital.'}
            {perfil === 'auditor_hospital' &&
              'Lança avaliações e gera Checklist só no hospital vinculado — não altera escopo, catálogo ou usuários.'}
            {perfil === 'operador' && 'Auditor da AMGestão: edita avaliações e escopo em todos os hospitais.'}
            {perfil === 'admin' && 'Acesso total ao sistema, incluindo usuários e auditoria.'}
          </div>
        </div>

        {exigeHospital(perfil) && (
          <div>
            <div className="am-field__label mb-1">Hospital vinculado</div>
            <SearchableSelect
              options={hospitalOpcoes}
              value={idHospital}
              onChange={(v) => setIdHospital(v)}
              placeholder="Selecione o hospital"
              required
            />
          </div>
        )}

        {usuario && (
          <button type="button" className="am-checkbox" onClick={() => setAtivo((v) => !v)}>
            <span className={`am-checkbox__box${ativo ? ' is-checked' : ''}`}>
              {ativo && <span className="ms">check</span>}
            </span>
            Usuário ativo (desmarcar bloqueia o acesso ao sistema)
          </button>
        )}

        {erro && <div className="am-field__error">{erro}</div>}

        <div className="am-form__actions">
          <button type="button" className="am-btn am-btn-outline" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="am-btn am-btn-dark" disabled={enviando}>
            {enviando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>

      {podeResetarSenha && (
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--am-borda)' }}>
          {!mostrarResetSenha ? (
            <button
              type="button"
              className="am-btn am-btn-outline"
              onClick={() => {
                setMostrarResetSenha(true);
                setResetSenhaSalva(false);
                setErroResetSenha('');
              }}
            >
              Redefinir senha
            </button>
          ) : (
            <div className="am-form">
              <div className="am-field__label mb-1">Redefinir senha de {usuario.nome}</div>
              <label className="am-field">
                <div className="am-field__label-row">
                  <span className="am-field__label">Nova senha</span>
                </div>
                <input
                  type="password"
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  minLength={8}
                  autoFocus
                />
              </label>
              <label className="am-field">
                <div className="am-field__label-row">
                  <span className="am-field__label">Confirmar nova senha</span>
                </div>
                <input
                  type="password"
                  value={confirmarNovaSenha}
                  onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                  minLength={8}
                />
              </label>

              {erroResetSenha && <div className="am-field__error">{erroResetSenha}</div>}

              <div className="am-form__actions">
                <button
                  type="button"
                  className="am-btn am-btn-outline"
                  onClick={() => {
                    setMostrarResetSenha(false);
                    setNovaSenha('');
                    setConfirmarNovaSenha('');
                    setErroResetSenha('');
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="am-btn am-btn-dark"
                  disabled={resetando}
                  onClick={handleResetarSenha}
                >
                  {resetando ? 'Redefinindo...' : 'Redefinir senha'}
                </button>
              </div>
            </div>
          )}

          {resetSenhaSalva && (
            <div style={{ fontSize: 13, color: 'var(--am-sucesso)', marginTop: 10 }}>
              Senha redefinida com sucesso.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
